// src/components/CompanyHeader.jsx (or wherever yours lives)
import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import InfoActionIcons from "../../InfoActionIcons";
import {
  buildCompanyProfileActions,
  getPrimaryCompanyWebsite,
} from "../../../utils/companySocialLinks";
import {
  buildEndpointUrls,
  resolvePublicAssetUrl,
  shouldTryEndpointFallbackStatus,
} from "../../../utils/apiUrls";

// --- tiny jwt decode (no library needed) ---
function decodeJwt(token) {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

const API_BASE = process.env.REACT_APP_API_BASE;
const CONTACT_ACTION_TYPES = new Set(["website", "email", "phone"]);
const SOCIAL_ACTION_TYPES = new Set([
  "linkedin",
  "facebook",
  "twitter",
  "x",
  "google",
  "instagram",
  "youtube",
  "tiktok",
]);

export default function CompanyHeader({
  company = {},
  leadId,            // pass this when possible
  companyId,
  subtitle,
  onLogoUpdated,     // optional callback(newUrl)
  sx = {},
}) {
  const token = localStorage.getItem("token") || "";
  const normalizedCompany = useMemo(
    () => {
      const rawCompany = company?.raw && typeof company.raw === "object" ? company.raw : {};
      return {
        ...rawCompany,
        ...company,
        id: company.id || company.company_id || rawCompany.id || rawCompany.company_id,
        name:
          company.full_name ||
          company.name ||
          company.company_name ||
          company.company ||
          company.candidate_name ||
          rawCompany.name ||
          rawCompany.company_name ||
          rawCompany.company ||
          rawCompany.full_name ||
          rawCompany.entity_name ||
          "",
        company_name:
          company.company_name ||
          company.name ||
          rawCompany.company_name ||
          rawCompany.name ||
          rawCompany.entity_name ||
          "",
        logo:
          company.avatar_url ||
          company.profile_image ||
          company.logo ||
          company.company_logo ||
          company.logo_url ||
          rawCompany.logo ||
          rawCompany.company_logo ||
          rawCompany.avatar_url ||
          rawCompany.profile_image ||
          rawCompany.entity_logo ||
          "",
        company_logo:
          company.avatar_url ||
          company.profile_image ||
          company.company_logo ||
          company.logo ||
          company.logo_url ||
          rawCompany.company_logo ||
          rawCompany.logo ||
          rawCompany.avatar_url ||
          rawCompany.profile_image ||
          rawCompany.entity_logo ||
          "",
        website:
          getPrimaryCompanyWebsite({
            ...rawCompany,
            ...company,
          }) ||
          company.website_url ||
          rawCompany.domain ||
          "",
        contact_email:
          company.contact_email || company.email || rawCompany.contact_email || rawCompany.email || "",
        contact_phone:
          company.contact_phone || company.phone || rawCompany.contact_phone || rawCompany.phone || "",
        linkedin_url: company.linkedin_url || rawCompany.linkedin_url || "",
        facebook_url: company.facebook_url || rawCompany.facebook_url || "",
        twitter_url: company.twitter_url || company.x_url || rawCompany.twitter_url || rawCompany.x_url || "",
      };
    },
    [company]
  );

  // ✅ role from token (most reliable)
  const role = useMemo(() => {
    const decoded = decodeJwt(token);
    const r = decoded?.role || localStorage.getItem("role") || "";
    return String(r).toLowerCase();
  }, [token]);

  const canEditLogo = role === "recruiter" || role === "administrator" || role === "admin" || role === "system_user";

  const idToSave =
    leadId ||
    company.leadId ||
    company.lead_id ||
    company.entity_id ||
    normalizedCompany.id;
  const persistedCompanyId = Number(companyId || 0);
  const isCompanyUpload = Number.isFinite(persistedCompanyId) && persistedCompanyId > 0;

  const name =
    normalizedCompany.name || normalizedCompany.company_name || "Company Name";
  const website =
    normalizedCompany.website || normalizedCompany.website_url || normalizedCompany.domain || "";
  const websiteLabel = useMemo(() => {
    const raw = String(website || "").trim();
    if (!raw) return "";

    try {
      const normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      return new URL(normalized).hostname.replace(/^www\./i, "");
    } catch {
      return raw.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "");
    }
  }, [website]);

  const profileActions = useMemo(() => {
    const seenTypes = new Set();

    return buildCompanyProfileActions(normalizedCompany).filter((action) => {
      const type = String(action?.type || "").trim().toLowerCase();
      if (!type || seenTypes.has(type)) return false;
      seenTypes.add(type);
      return true;
    });
  }, [normalizedCompany]);
  const contactActions = useMemo(
    () => profileActions.filter((action) => CONTACT_ACTION_TYPES.has(action?.type)),
    [profileActions]
  );
  const socialActions = useMemo(
    () => profileActions.filter((action) => SOCIAL_ACTION_TYPES.has(action?.type)),
    [profileActions]
  );

  // ✅ local logo state
  const resolveLogoUrl = useCallback((raw) => {
    // Use the API proxy for uploaded files. Production Nginx may intercept
    // `/uploads` before it reaches Express, while `/api/uploads` is reliably
    // served by the backend.
    return resolvePublicAssetUrl(raw, {
      apiBase: API_BASE,
      preferApiProxyForUploads: true,
    });
  }, []);

  const [logoUrl, setLogoUrl] = useState(
    resolveLogoUrl(normalizedCompany.company_logo || normalizedCompany.logo || "")
  );
  const isSvgLogo = useMemo(
    () => /(?:\.svg)(?:[?#]|$)|^data:image\/svg\+xml/i.test(String(logoUrl || "")),
    [logoUrl]
  );
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  // Parent/profile refreshes can briefly carry the pre-upload record.  Keep a
  // server-confirmed upload displayed until the parent catches up with it.
  const confirmedLogoUrlRef = useRef("");

  useEffect(() => {
    const incomingLogoUrl = resolveLogoUrl(
      normalizedCompany.company_logo || normalizedCompany.logo || ""
    );
    const confirmedLogoUrl = confirmedLogoUrlRef.current;

    if (confirmedLogoUrl) {
      if (incomingLogoUrl === confirmedLogoUrl) {
        confirmedLogoUrlRef.current = "";
      } else {
        setLogoUrl(confirmedLogoUrl);
        return;
      }
    }

    setLogoUrl(incomingLogoUrl);
  }, [normalizedCompany.company_logo, normalizedCompany.logo, normalizedCompany.id, resolveLogoUrl]);

  // hidden file input
  const fileRef = useRef(null);

  const openPicker = useCallback(() => {
    if (!canEditLogo) return;
    if (!token) {
      console.error("No token found. User must be logged in.");
      return;
    }
    if (!idToSave) {
      console.error("No leadId/company.id provided. Cannot upload logo.");
      return;
    }
    // ✅ ensure the input exists + click it
    if (fileRef.current) fileRef.current.click();
  }, [canEditLogo, token, idToSave]);

  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      // reset input so choosing same file again still triggers change
      e.target.value = "";
      if (!file) return;
      setUploadError("");

      const okType = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
      ].includes(file.type);
      if (!okType) {
        setUploadError("Logo must be PNG, JPG, or WebP.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        console.error("Logo too large (max 5MB).");
        return;
      }

      // optimistic preview
      const preview = URL.createObjectURL(file);
      setLogoUrl(preview);

      try {
        setUploading(true);

        const fd = new FormData();
        // ✅ backend route below expects "image"
        fd.append("image", file);

        const endpointPath = isCompanyUpload
          ? `/upload/company-logo/company/${persistedCompanyId}`
          : `/upload/company-logo/${idToSave}`;
        const uploadUrls = buildEndpointUrls(
          API_BASE,
          `/api${endpointPath}`,
          endpointPath
        );
        let res = null;
        let lastError = null;

        for (const uploadUrl of uploadUrls) {
          try {
            const response = await fetch(uploadUrl, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
              },
              credentials: "include",
              body: fd,
            });
            res = response;
            if (response.ok || !shouldTryEndpointFallbackStatus(response.status)) break;
          } catch (error) {
            lastError = error;
          }
        }

        if (!res?.ok) {
          const payload = res ? await res.json().catch(() => ({})) : {};
          throw new Error(
            payload?.error ||
              lastError?.message ||
              `Logo upload failed${res ? ` (${res.status})` : ""}.`
          );
        }

        const json = await res.json().catch(() => ({}));
        // The legacy upload route returns `url`, while the company endpoint
        // confirms the saved value as `logo`/`persisted_logo`.  Accept every
        // supported response shape so a successful upload is never reverted
        // in the UI just because the response uses the other field name.
        const newUrl =
          json.persisted_logo ||
          json.logo ||
          json.company_logo ||
          json.logo_url ||
          json.url;

        if (newUrl) {
          const confirmedLogoUrl = resolveLogoUrl(newUrl);
          confirmedLogoUrlRef.current = confirmedLogoUrl;
          setLogoUrl(confirmedLogoUrl);
          onLogoUpdated?.(confirmedLogoUrl);
        } else {
          throw new Error("The server accepted the image but did not confirm it was saved.");
        }
      } catch (err) {
        console.error("Logo upload error:", err);
        setUploadError(err?.message || "Failed to save company logo.");
        confirmedLogoUrlRef.current = "";
        setLogoUrl(resolveLogoUrl(normalizedCompany.company_logo || normalizedCompany.logo || ""));
      } finally {
        setUploading(false);
        if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
      }
    },
    [
      idToSave,
      isCompanyUpload,
      persistedCompanyId,
      token,
      normalizedCompany.company_logo,
      normalizedCompany.logo,
      onLogoUpdated,
      resolveLogoUrl,
    ]
  );

  const editable = canEditLogo && !!token && !!idToSave;

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "stretch", md: "center" },
        gap: 1.25,
        mt: 0.5,
        mb: 2.25,
        py: 0.9,
        px: 0,
        ...sx,
      }}
    >
      {/* ✅ Hidden file input MUST exist in DOM */}
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* LEFT SECTION */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          flexWrap: "wrap",
          rowGap: 1,
          columnGap: 1.35,
          minWidth: 0,
          width: "100%",
        }}
      >
        {/* Avatar */}
        <Tooltip
          title={
            !editable
              ? ""
              : uploading
              ? "Uploading..."
              : "Click to change company logo"
          }
          disableHoverListener={!editable}
        >
          <Box
            onClick={openPicker}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: editable ? "pointer" : "default",
              position: "relative",
              borderRadius: "999px",
              outline: editable ? "2px solid rgba(124,58,237,0.15)" : "none",
              outlineOffset: 2,
            }}
          >
            <Avatar
              src={logoUrl || ""}
              imgProps={{
                style: {
                  objectFit: isSvgLogo ? "contain" : "cover",
                  padding: isSvgLogo ? "4px" : 0,
                  boxSizing: "border-box",
                },
              }}
              sx={{
                width: 40,
                height: 40,
                // A pale purple surface keeps a white or transparent-white logo
                // visible without changing the artwork itself. Opaque logos
                // continue to cover this background normally.
                background: logoUrl ? "#F3E8FF" : "linear-gradient(135deg,#60a5fa,#4338ca)",
                border: logoUrl ? "1px solid #E5E7EB" : "none",
                fontSize: 18,
                fontWeight: 700,
                opacity: uploading ? 0.65 : 1,
              }}
            >
              {!logoUrl && name?.[0]}
            </Avatar>

            {uploading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <CircularProgress size={18} />
              </Box>
            )}
          </Box>
        </Tooltip>

        {/* Name + profile subtitle */}
        <Box sx={{ minWidth: 0, maxWidth: "100%", flex: "0 1 auto" }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: "#667085",
              lineHeight: 1.15,
              fontFamily: "Be Vietnam Pro",
            }}
          >
            {name}
          </Typography>

          <Typography
            noWrap={subtitle !== undefined}
            title={subtitle}
            sx={{
              color: "#9C4FFF",
              fontSize: 14,
              mt: 0.25,
              lineHeight: 1.15,
              fontFamily: "Be Vietnam Pro",
            }}
          >
            {(subtitle ?? websiteLabel) || "-"}
          </Typography>
        </Box>

        {/* divider */}
        <Box
          sx={{
            width: "1px",
            height: 26,
            backgroundColor: "#CBD5E1",
            display: "block",
            flex: "0 0 auto",
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.1,
            minWidth: 0,
            maxWidth: "100%",
            flexWrap: "wrap",
          }}
        >
          <InfoActionIcons
            actions={contactActions}
            gap={1.1}
            showDisabled={true}
            showGroupDivider={false}
            preserveOrder={true}
          />

          {contactActions.length && socialActions.length ? (
            <Box
              aria-hidden="true"
              sx={{
                width: "1px",
                height: 20,
                backgroundColor: "#CBD5E1",
                flex: "0 0 auto",
              }}
            />
          ) : null}

          <InfoActionIcons
            actions={socialActions}
            gap={1.1}
            showDisabled={true}
            showGroupDivider={false}
            preserveOrder={true}
          />
        </Box>
        {uploadError ? (
          <Alert severity="error" sx={{ mt: 1, width: "100%" }}>
            {uploadError}
          </Alert>
        ) : null}
      </Box>

    </Box>
  );
}
